import React, { useState, useEffect, useRef } from "react";
import { Badge } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { useTranslate } from '@tolgee/react';

import 'react-bootstrap-typeahead/css/Typeahead.css';

interface LomavisTypeaheadSelectInput1Item {
    id: number | string;
    name: string;
}

interface LomavisTypeaheadSelectInput1Props {
    /** Unique id for the typeahead input */
    id?: string;
    /** Function that returns the available options for selection */
    getOptions: () => LomavisTypeaheadSelectInput1Item[];
    /** Callback function called when items are selected or removed */
    onSelectionChange: (newArray: LomavisTypeaheadSelectInput1Item[], action: 'selected' | 'removed') => void;
    /** Initial selected values */
    initialValues?: LomavisTypeaheadSelectInput1Item[];
    /** Placeholder text for the input */
    placeholder?: string;
    /** Whether the component is disabled */
    disabled?: boolean;
    /** Title text displayed above the component */
    title?: string;
    /** Helper text displayed below the input field */
    subtitle?: string;
    /** Optional callback for input change (for async search) - receives query and setLoading function */
    onInputChange?: (query: string, setLoading: (loading: boolean) => void) => void;
    /** Minimum length before triggering onInputChange */
    minLength?: number;
    /** Render selected items inside the typeahead input instead of below it */
    showSelectedInInput?: boolean;
    /** Debounce async input changes by this many milliseconds */
    debounceMs?: number;
    /** Render the menu fixed to the viewport so it can escape clipped parents */
    positionFixed?: boolean;
    /** Allow the menu to flip above the input when space is constrained */
    flip?: boolean;
    /** Maximum number of menu results to show */
    maxResults?: number;
    /** Whether to show pagination in long menus */
    paginate?: boolean;
}

/**
* Atoms: LomavisTypeaheadSelectInput1
* 
* A typeahead select input that allows multiple selections and displays them as removable badges.
* - Uses Typeahead component for selection
* - Shows selected items as badges below the input with white × buttons for removal
* - Supports optional title and helper text display
* - Supports initial values and callback on selection changes
* - Includes internal loading state management for async operations
* - onInputChange callback receives query string and setLoading function for async loading control
* - Automatically shows loading spinner and disables input during async operations
*/
export const LomavisTypeaheadSelectInput1: React.FC<LomavisTypeaheadSelectInput1Props> = ({ 
    id = "lomavis-typeahead-select-input",
    getOptions, 
    onSelectionChange, 
    initialValues = [], 
    placeholder = "Select items...",
    disabled = false,
    title,
    subtitle,
    onInputChange,
    minLength = 2,
    showSelectedInInput = false,
    debounceMs = 0,
    positionFixed = false,
    flip = false,
    maxResults,
    paginate = true
}) => {
    
    const { t } = useTranslate();
    const [selectedItems, setSelectedItems] = useState<LomavisTypeaheadSelectInput1Item[]>(initialValues);
    const [isLoading, setIsLoading] = useState(false);
    const prevInitialValuesRef = useRef(initialValues);
    const typeaheadRef = useRef<any>(null);
    const debounceTimeoutRef = useRef<number | null>(null);

    // Update selected items when initialValues change, but only if they're actually different
    useEffect(() => {
        const prevInitialValues = prevInitialValuesRef.current;
        
        // Check if initialValues actually changed
        const hasChanged = initialValues.length !== prevInitialValues.length || 
            initialValues.some(item => !prevInitialValues.find(prev => prev.id === item.id));
        
        if (hasChanged) {
            setSelectedItems(initialValues);
            prevInitialValuesRef.current = initialValues;
        }
    }, [initialValues]);

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current !== null) {
                window.clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    // Handle selection from typeahead
    const handleTypeaheadChange = (selected: any[]) => {
        if (showSelectedInInput) {
            const newSelectedItems = selected as LomavisTypeaheadSelectInput1Item[];
            const action = newSelectedItems.length >= selectedItems.length ? 'selected' : 'removed';
            setSelectedItems(newSelectedItems);
            onSelectionChange(newSelectedItems, action);
            return;
        }

        if (selected.length > 0) {
            const newItem = selected[0] as LomavisTypeaheadSelectInput1Item;
            // Check if item is already selected
            const isAlreadySelected = selectedItems.some(item => item.id === newItem.id);
            
            if (!isAlreadySelected) {
                const newSelectedItems = [...selectedItems, newItem];
                setSelectedItems(newSelectedItems);
                onSelectionChange(newSelectedItems, 'selected');
                typeaheadRef.current?.clear();
            }
        }
    };

    // Handle removal of selected item
    const handleRemoveItem = (itemToRemove: LomavisTypeaheadSelectInput1Item) => {
        const newSelectedItems = selectedItems.filter(item => item.id !== itemToRemove.id);
        setSelectedItems(newSelectedItems);
        onSelectionChange(newSelectedItems, 'removed');
    };

    const handleInputChange = (query: string) => {
        if (!onInputChange) return;

        if (debounceTimeoutRef.current !== null) {
            window.clearTimeout(debounceTimeoutRef.current);
            debounceTimeoutRef.current = null;
        }

        if (query.length < minLength) {
            setIsLoading(false);
            return;
        }

        const runSearch = () => {
            onInputChange(query, setIsLoading);
        };

        if (debounceMs > 0) {
            debounceTimeoutRef.current = window.setTimeout(runSearch, debounceMs);
        } else {
            runSearch();
        }
    };

    // Filter out already selected items from options
    const availableOptions = getOptions().filter(option => 
        !selectedItems.some(selected => selected.id === option.id)
    );

    return (
        <div className="mb-3">
                {/* Title */}
                {title && (
                    <label className="form-label fw-bold">{title}</label>
                )}

                {/* Typeahead Input */}
                <div className="position-relative">
                    <Typeahead
                        ref={typeaheadRef}
                        id={id}
                        labelKey="name"
                        options={availableOptions}
                        placeholder={isLoading ? t('lomavisSharedUi.typeaheadSelectInput.loading') : placeholder}
                        
                        selected={showSelectedInInput ? selectedItems : []}
                        multiple={showSelectedInInput}
                        onChange={handleTypeaheadChange}
                        filterBy={onInputChange ? () => true : undefined}
                        clearButton
                        disabled={disabled}
                        onInputChange={onInputChange ? handleInputChange : undefined}
                        minLength={onInputChange ? minLength : 0}
                        isLoading={isLoading}
                        emptyLabel={isLoading ? t('lomavisSharedUi.typeaheadSelectInput.loading') : t('lomavisSharedUi.typeaheadSelectInput.noMatchesFound')}
                        flip={flip}
                        maxResults={maxResults}
                        paginate={paginate}
                        positionFixed={positionFixed}
                    />
                    {isLoading && (
                        <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">{t('lomavisSharedUi.typeaheadSelectInput.loading')}</span>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Subtitle */}
                {subtitle && (
                    <small className="text-muted">{subtitle}</small>
                )}
                
                {/* Selected Items as Badges */}
                {!showSelectedInInput && selectedItems.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mt-2">
                        {selectedItems.map((item) => (
                            <Badge 
                                key={item.id}
                                bg="primary" 
                                className="d-flex align-items-center gap-2 px-3 py-2"
                                style={{ fontSize: '0.875rem' }}
                            >
                                <span>{item.name}</span>
                                <i 
                                    className="fa-duotone fa-times cursor-pointer text-white"
                                    onClick={() => handleRemoveItem(item)}
                                    style={{ fontSize: '0.75rem' }}
                                    role="button"
                                    aria-label={t('lomavisSharedUi.typeaheadSelectInput.removeItem', { itemName: item.name })}
                                />
                            </Badge>
                        ))}
                    </div>
                )}
        </div>
    );
};
